import { AlertTriangle, Boxes, CheckCircle2, Clock3, RefreshCw, ShieldAlert } from "lucide-react";
import { can } from "../../../shared/auth/access";
import type { useMaterialPreparationWorkspace } from "./useMaterialPreparationWorkspace";
import { statusLabels, readinessLabels, ownerLabels, messageLabels, historyLabels, historyStateSummary, qty } from "./preparation-presentation";
import { ExceptionOverride } from "./ExceptionOverride";
import { RecheckButton } from "./RecheckButton";
import { WorkflowComposer } from "./WorkflowComposer";
import { PhysicalCountActions } from "./PhysicalCountActions";
export function MaterialPreparationView({user, notify, workspace}: {
 user: Parameters<typeof useMaterialPreparationWorkspace>[0]["user"];
 notify: Parameters<typeof useMaterialPreparationWorkspace>[0]["notify"];
 workspace: ReturnType<typeof useMaterialPreparationWorkspace>;
}) {
  const { checks, setChecks, loading, error, load, emptyState } = workspace;
  return (
    <section
      className="material-preparation-panel"
      aria-labelledby="material-preparation-title"
    >
      <header>
        <div>
          <span>ÜRETİM ÖNCESİ HAZIRLIK</span>
          <h2 id="material-preparation-title">Malzeme teyitleri</h2>
          <p>Yalnız eksik, farklı veya teyit bekleyen kalemler</p>
        </div>
        <button
          type="button"
          className="material-preparation-refresh"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw size={16} />
          {loading ? "Kontrol ediliyor" : "Yenile"}
        </button>
      </header>
      {loading ? (
        <div className="material-preparation-state" role="status">
          <Clock3 size={20} />
          Güncel kontroller alınıyor…
        </div>
      ) : error ? (
        <div className="material-preparation-state is-error" role="alert">
          <AlertTriangle size={20} />
          <div>
            <strong>Kontroller alınamadı</strong>
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              notify("Kontroller yeniden yükleniyor.");
              void load();
            }}
          >
            Tekrar dene
          </button>
        </div>
      ) : checks.length === 0 ? (
        <div className="material-preparation-state is-empty">
          {emptyState.kind === "READY" ||
          emptyState.kind === "NO_OPEN_ACTION" ? (
            <CheckCircle2 size={22} />
          ) : (
            <Clock3 size={22} />
          )}
          <div>
            <strong>{emptyState.title}</strong>
            <span>{emptyState.detail}</span>
          </div>
        </div>
      ) : (
        <div className="material-preparation-checks">
          {checks.map((check) => {
            const differences = check.items.flatMap((item) =>
              item.differences.map((difference) => ({ item, difference })),
            );
            return (
              <article
                key={check.id}
                id={`material-preparation-check-${check.id}`}
                tabIndex={-1}
                className="material-preparation-check"
              >
                <header>
                  <div>
                    <span>
                      {check.workOrderNo} · {check.productCode}
                    </span>
                    <h3>{check.productName}</h3>
                    <p>
                      {check.plannedStartAt
                        ? new Intl.DateTimeFormat("tr-TR", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          }).format(new Date(check.plannedStartAt))
                        : "Plan saati belirtilmemiş"}{" "}
                      · {qty(check.productionQuantity, check.productionUnit)}
                    </p>
                  </div>
                  <div className="preparation-check-actions">
                    <strong
                      className={`preparation-status is-${check.status.toLowerCase()}`}
                    >
                      {check.override
                        ? "İstisna ile kapatıldı"
                        : (readinessLabels[check.readinessState] ??
                          statusLabels[check.status] ??
                          check.status)}
                    </strong>
                    {can(user, "material.preparation.workflow") ? (
                      <RecheckButton
                        check={check}
                        notify={notify}
                        onSaved={() => void load()}
                      />
                    ) : null}
                    {can(user, "material.preparation.override") &&
                    !check.override ? (
                      <ExceptionOverride
                        check={check}
                        notify={notify}
                        onSaved={() => void load()}
                      />
                    ) : null}
                  </div>
                </header>
                {check.override ? (
                  <div className="preparation-override-notice">
                    <ShieldAlert size={17} />
                    <span>
                      <strong>{check.override.userName}</strong> ·{" "}
                      {check.override.reason} ·{" "}
                      {new Intl.DateTimeFormat("tr-TR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      }).format(new Date(check.override.at))}
                    </span>
                  </div>
                ) : null}
                <div className="preparation-warehouse-line">
                  <Boxes size={17} />
                  <span>
                    Ana depo:{" "}
                    <strong>
                      {check.mainWarehouse.name || check.mainWarehouse.code}
                    </strong>
                  </span>
                  <span>
                    Teslim / tesellüm:{" "}
                    <strong>
                      {check.handoffWarehouse.name ||
                        check.handoffWarehouse.code}
                    </strong>
                  </span>
                  <span>
                    Cep depo:{" "}
                    <strong>
                      {check.stagingWarehouse.name ||
                        check.stagingWarehouse.code}
                    </strong>
                  </span>
                </div>
                <div className="preparation-warehouse-line">
                  <Clock3 size={17} />
                  <span>
                    Reçete:{" "}
                    <strong>
                      {check.recipeSource === "MICRO_ERP"
                        ? "Mikro ERP"
                        : check.recipeSource}
                    </strong>{" "}
                    · içerik sürümü <strong>{check.recipeVersion}</strong>
                  </span>
                  <span>
                    Mikro gözlemi:{" "}
                    <strong>
                      {new Intl.DateTimeFormat("tr-TR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      }).format(new Date(check.sourceObservedAt))}
                    </strong>
                  </span>
                  <span>
                    Kontrol revizyonu <strong>{check.revision}</strong> · kayıt
                    sürümü <strong>{check.version}</strong>
                  </span>
                </div>
                {check.messages.length ? (
                  <section
                    className="preparation-message-history"
                    aria-label="Depo iletişim geçmişi"
                  >
                    <strong>Son depo iletişimleri</strong>
                    <ol>
                      {check.messages.slice(-5).map((message) => (
                        <li key={message.id}>
                          <span>
                            {messageLabels[message.type] ?? message.type} ·{" "}
                            {message.createdBy}
                          </span>
                          <div>
                            <p>{message.message}</p>
                            {message.materialCode ? (
                              <small>
                                {message.materialCode}
                                {message.requestedQuantity !== null
                                  ? ` · ${qty(message.requestedQuantity, message.unit ?? "")}`
                                  : ""}
                              </small>
                            ) : null}
                          </div>
                          <time dateTime={message.createdAt}>
                            {new Intl.DateTimeFormat("tr-TR", {
                              dateStyle: "short",
                              timeStyle: "short",
                            }).format(new Date(message.createdAt))}
                          </time>
                        </li>
                      ))}
                    </ol>
                  </section>
                ) : null}
                <div className="preparation-differences">
                  {differences.map(({ item, difference }, index) => (
                    <div
                      key={`${item.materialCode}-${difference.code}-${index}`}
                      className={`preparation-difference is-${difference.severity.toLowerCase()}`}
                    >
                      <AlertTriangle size={18} />
                      <div className="preparation-material">
                        <strong>
                          {item.materialCode} · {item.materialName}
                        </strong>
                        <span>
                          İhtiyaç {qty(item.requiredQuantity, item.unit)} · Ana{" "}
                          {qty(item.mainSystemQuantity, item.unit)} · Teslim{" "}
                          {qty(item.handoffSystemQuantity, item.unit)} · Cep{" "}
                          {qty(item.stagingSystemQuantity, item.unit)}
                          {item.offRouteSystemQuantity > 0
                            ? ` · Rota dışı ${qty(item.offRouteSystemQuantity, item.unit)} (${item.offRouteWarehouseCount} depo)`
                            : ""}
                        </span>
                      </div>
                      <div>
                        <small>PROBLEM / NEDEN</small>
                        <strong>{difference.reason}</strong>
                      </div>
                      <div>
                        <small>SORUMLU / AKSİYON</small>
                        <strong>
                          {ownerLabels[difference.owner] ?? difference.owner}
                        </strong>
                        <span>{difference.action}</span>
                      </div>
                      {difference.quantity !== null ? (
                        <b>{qty(Math.abs(difference.quantity), item.unit)}</b>
                      ) : null}
                    </div>
                  ))}
                  {check.items.map((item) => (
                    <PhysicalCountActions
                      key={`count-${item.materialCode}`}
                      check={check}
                      item={item}
                      user={user}
                      onSaved={() => {
                        void load();
                        notify(
                          `${item.materialCode} fiziki sayımı kaydedildi.`,
                        );
                      }}
                      notify={notify}
                    />
                  ))}
                </div>
                {can(user, "material.preparation.workflow") ? (
                  <WorkflowComposer
                    check={check}
                    notify={notify}
                    onSaved={(next) => {
                      setChecks((current) =>
                        current.map((candidate) =>
                          candidate.id === next.id ? next : candidate,
                        ),
                      );
                      notify("Malzeme iş akışı kaydedildi.");
                    }}
                  />
                ) : null}
                {check.history.length ? (
                  <details className="preparation-audit-history">
                    <summary>
                      Denetim geçmişi · {check.history.length} kayıt
                    </summary>
                    <ol>
                      {check.history
                        .slice(-8)
                        .reverse()
                        .map((event) => {
                          const previous = historyStateSummary(
                            event.previousState,
                          );
                          const next = historyStateSummary(event.nextState);
                          return (
                            <li key={event.id}>
                              <strong>
                                {historyLabels[event.eventType] ??
                                  event.eventType}
                              </strong>
                              <span>{event.actorName}</span>
                              {previous ? <p>Önce: {previous}</p> : null}
                              {next ? <p>Sonra: {next}</p> : null}
                              {event.reason ? <p>{event.reason}</p> : null}
                              <time dateTime={event.occurredAt}>
                                {new Intl.DateTimeFormat("tr-TR", {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                }).format(new Date(event.occurredAt))}
                              </time>
                            </li>
                          );
                        })}
                    </ol>
                  </details>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
